import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, X, Calendar, Unlock, Sparkles, CheckCheck, Clock } from "lucide-react";
import axios from "axios";

/**
 * Topbar notification bell.
 * Derives notifications from existing endpoints (no new DB):
 *  - Upcoming sessions within 7 days
 *  - Newly unlocked organs (since last "Mark all read")
 *  - New quizzes (since last "Mark all read")
 *
 * Read state = single timestamp `bss.notif.lastSeenAt` (ms since epoch).
 * Item is "unread" if its event time > lastSeenAt. Polls every 60s.
 */
export const NotificationBell = ({ apiUrl, lang = "ms", onNavigate }) => {
  const isEn = lang === "en";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    try {
      return parseInt(localStorage.getItem("bss.notif.lastSeenAt") || "0", 10);
    } catch (_e) {
      return 0;
    }
  });
  // First-mount timestamp - anything created before this is "history" and not unread on first load
  const firstMountAtRef = useRef(Date.now());
  const popoverRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    const out = [];
    const now = Date.now();
    const dayMs = 86400000;

    // 1) Upcoming sessions (within next 7 days, including ones that started in last 2 hours)
    try {
      const evRes = await axios.get(`${apiUrl}/events/upcoming`);
      const evs = Array.isArray(evRes.data) ? evRes.data : (evRes.data?.events || []);
      evs.forEach((s) => {
        const dt = s.date ? new Date(s.date).getTime() : 0;
        if (dt && dt - now < 7 * dayMs && dt > now - 2 * 60 * 60 * 1000) {
          out.push({
            id: `event-${s.id}`,
            type: "event",
            title: isEn ? "Upcoming Session" : "Sesi Akan Datang",
            body: `${s.topic || (isEn ? "Class session" : "Sesi kelas")}${s.location ? ` · ${s.location}` : ""}`,
            // Event "time" = creation/seen time, not session date
            time: dt,
            section: "events",
          });
        }
      });
    } catch (_e) { /* noop */ }

    // 2) Unlocked organs - show one per unlocked organ; "unread" computed via lastSeenAt
    try {
      const acRes = await axios.get(`${apiUrl}/me/organ-access`);
      const unlocked = acRes.data?.unlocked || [];
      unlocked.forEach((n) => {
        // No per-organ unlock timestamp from API; default to firstMount.
        // Skip navigation (organ ids are UUIDs not exposed here) - sidebar is one click away.
        out.push({
          id: `organ-${n}`,
          type: "organ",
          title: isEn ? `Organ ${n} unlocked` : `Organ ${n} telah dibuka`,
          body: isEn ? "Open from sidebar to start learning" : "Buka dari menu sisi untuk mula belajar",
          time: firstMountAtRef.current,
        });
      });
    } catch (_e) { /* noop */ }

    // 3) Quizzes created within last 30 days
    try {
      const qzRes = await axios.get(`${apiUrl}/quizzes`);
      const quizzes = qzRes.data || [];
      quizzes.forEach((q) => {
        const ts = q.created_at ? new Date(q.created_at).getTime() : firstMountAtRef.current;
        if (now - ts < 30 * dayMs) {
          out.push({
            id: `quiz-${q.id}`,
            type: "quiz",
            title: isEn ? "Quiz available" : "Quiz tersedia",
            body: q.title || "Quiz",
            time: ts,
          });
        }
      });
    } catch (_e) { /* noop */ }

    out.sort((a, b) => b.time - a.time);
    setItems(out.slice(0, 30));
  }, [apiUrl, isEn]);

  useEffect(() => {
    loadNotifications();
    const intervalId = setInterval(loadNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [loadNotifications]);

  // Outside-click close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unreadCount = items.filter((it) => it.time > lastSeenAt).length;

  const markAllRead = () => {
    const t = Date.now();
    setLastSeenAt(t);
    try { localStorage.setItem("bss.notif.lastSeenAt", String(t)); } catch (_e) { /* noop */ }
  };

  const handleItemClick = (item) => {
    if (item.section && onNavigate) {
      onNavigate(item.section);
    }
    setOpen(false);
    markAllRead();
  };

  const fmtTime = (ts) => {
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60000);
    if (diff < 0) {
      const futureMin = Math.floor(-diff / 60000);
      if (futureMin < 60) return isEn ? `in ${futureMin}m` : `${futureMin}m lagi`;
      const futureHr = Math.floor(futureMin / 60);
      if (futureHr < 24) return isEn ? `in ${futureHr}h` : `${futureHr}j lagi`;
      return isEn ? `in ${Math.floor(futureHr / 24)}d` : `${Math.floor(futureHr / 24)}h lagi`;
    }
    if (min < 1) return isEn ? "just now" : "baru saja";
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    return `${Math.floor(hr / 24)}d`;
  };

  const iconFor = (type) => {
    if (type === "event") return <Calendar size={14} className="text-[#2563EB]" />;
    if (type === "organ") return <Unlock size={14} className="text-green-600" />;
    if (type === "quiz") return <Sparkles size={14} className="text-amber-500" />;
    return <Bell size={14} className="text-[#0A0A0A]" />;
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-100"
        data-testid="notification-bell-btn"
        title={isEn ? "Notifications" : "Notifikasi"}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[340px] sm:w-[380px] max-h-[480px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-[rgba(0,0,0,0.06)] dark:border-gray-700 z-50 flex flex-col overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(0,0,0,0.06)] dark:border-gray-700">
            <h3 className="text-sm font-bold text-[#0A0A0A] dark:text-gray-100">
              {isEn ? "Notifications" : "Notifikasi"}
            </h3>
            <div className="flex items-center gap-1">
              {items.length > 0 && unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#2563EB] hover:bg-[#EFF6FF] px-2 py-1 rounded-md"
                  data-testid="notification-mark-all-read"
                >
                  <CheckCheck size={12} /> {isEn ? "Mark all read" : "Tanda baca"}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 rounded-full hover:bg-[#F7F7F7] dark:hover:bg-gray-800 flex items-center justify-center"
                data-testid="notification-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-[#F7F7F7] dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
                  <Bell size={20} className="text-gray-400" />
                </div>
                <p className="text-xs text-[rgba(0,0,0,0.45)] dark:text-gray-400">
                  {isEn ? "You're all caught up!" : "Tiada notifikasi baharu"}
                </p>
              </div>
            ) : (
              items.map((it) => {
                const unread = it.time > lastSeenAt;
                return (
                  <button
                    key={it.id}
                    onClick={() => handleItemClick(it)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-[#FAFAFA] dark:hover:bg-gray-800/60 transition-colors text-left border-b border-[rgba(0,0,0,0.04)] dark:border-gray-800 ${unread ? "bg-[#F0F7FF]/40 dark:bg-blue-900/10" : ""}`}
                    data-testid="notification-item"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#F7F7F7] dark:bg-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {iconFor(it.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${unread ? "font-bold" : "font-semibold"} text-[#0A0A0A] dark:text-gray-100 truncate`}>
                          {it.title}
                        </p>
                        <span className="text-[10px] text-[rgba(0,0,0,0.4)] dark:text-gray-500 flex items-center gap-0.5 flex-shrink-0">
                          <Clock size={9} /> {fmtTime(it.time)}
                        </span>
                      </div>
                      <p className="text-xs text-[rgba(0,0,0,0.55)] dark:text-gray-400 mt-0.5 line-clamp-2">
                        {it.body}
                      </p>
                    </div>
                    {unread && <span className="w-2 h-2 rounded-full bg-[#2563EB] flex-shrink-0 mt-2" />}
                  </button>
                );
              })
            )}
          </div>
          {items.length > 0 && (
            <button
              onClick={() => { setOpen(false); if (onNavigate) onNavigate('notifications'); }}
              className="w-full px-4 py-2.5 text-xs font-semibold text-[#2563EB] hover:bg-[#EFF6FF] border-t border-[#F1F5F9] transition-colors"
              data-testid="notification-see-all"
            >
              {isEn ? 'See all notifications →' : 'Lihat semua pemberitahuan →'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
